import { browserHistory } from "react-router";
import Globalize from "globalize";
import cookie from "react-cookie";
import _ from "lodash";
import BaseModel from "lib/model";
import app from "lib/app";
import Session from "./me/session";
import config from "../site-config";
import * as Sentry from "@sentry/browser"; // error tacking
import { userLogout } from "actions/user";
import { clearInsightsData } from "actions/insights-actions";
import { toggleLoginDialog } from "actions/app";
import { activeBrandClear, activeCompanyClear } from "actions/scope";
import { NO_AUTH, PARTIAL_AUTH, FULL_AUTH, isValidAuthStatus } from "constants/auth-status";
import { getStore } from "store";
import { ConnectionStatus } from "../records/connection-request";
import { DRIVE_LINK } from "containers/folders/utils/constants";
import { LAUNCH_DARKLY_ANONYMOUS_USER_KEY } from "../constants/login";
import { logoutChurnZero } from "lib/churn-zero";
import { AccountFeatureFlags } from "../constants/lisd-feature-flags";

const LAST_MERCHANT_ID = "LAST_MERCHANT_ID";
const Me = BaseModel.extend({
    session: {
        authStatus: {
            type: "number",
            required: true,
            default: NO_AUTH,
        },
        redirectTo: "string",
    },

    // We dont need to ever pass the id since this is me
    url: "/me",

    props: {
        _id: "objectId",
        name: "string",
        email: "string",
        email_hash: "string",
        account_type: "string",
        account_group: "string",
        created_on: "string",
        locale: { type: "string", default: "en-US" },
        available_modules: {
            type: "array",
            default: () => [],
            required: true,
        },
        last_brand: "string",
        last_assortment_view: {
            type: "string",
            default: () => "tile",
        },
        login_managed_by: "string",
        intercom_hash: "string",
        retailers: {
            type: "array",
            default: () => [],
            required: true,
        },
        external_references: {
            // todo: flesh out with full on schema?
            type: "array",
            default: () => [],
            required: true,
        },
        buyer_form_submitted: "boolean",
        using_temp_password: "boolean",
        new_product_details: "boolean",
        build_order: "boolean",
        new_contacts: "boolean",
        access_collections: "boolean",
        access_merchants: "boolean",
        has_merchant_sso_config: "boolean",
        view_preferences: {
            type: "object",
            default: () => {
                /* no-op */
            },
            required: false,
        },
        merchant_connections: {
            type: "array",
            default: function () {
                return [];
            },
            required: true,
        },
        last_login: "string",
        connection_requests: {
            type: "array",
            default: () => [],
            required: false,
        },
        actual_user: { type: "object", required: false },
        site_connections: { type: "array", default: () => [], required: false },
        source: { type: "string", required: false },
        permissions: {
            type: "array",
            default: () => [],
        },
    },

    derived: {
        _initials: {
            deps: ["name"],
            cache: false,
            fn() {
                let initals = "";
                if (this.name) {
                    const chunks = this.name.split(" ");
                    for (let i = 0; i < chunks.length; i++) {
                        if (i >= 2) {
                            break;
                        }
                        initals = initals + (chunks[i][0] || "");
                    }
                }

                return initals.toUpperCase();
            },
        },
        _firstName: {
            deps: ["name"],
            cache: true,
            fn() {
                if (this.name) {
                    return this.name.split(" ")[0];
                }
                return "";
            },
        },
        _activeCurrency: {
            cache: false,
            fn() {
                const { scope } = getStore().getState();
                return scope.currencyCode || (scope.brand && scope.brand.settings.default_currency);
            },
        },

        _activeMSRPCurrency: {
            cache: false,
            fn() {
                const { scope } = getStore().getState();
                if (!scope.brand?.hasPreference?.("separate_msrp_currency")) {
                    return this._activeCurrency;
                }
                return (
                    (scope.brand?.hasPreference?.("use_start_order") && scope.msrpCurrencyCode) ||
                    scope?.viewAs?.__msrp_currency_codes?.[0] ||
                    scope?.company?.__msrp_currency_codes?.[0] ||
                    this._activeCurrency
                );
            },
        },

        _activePriceTemplate: {
            cache: false,
            fn() {
                const { scope } = getStore().getState();
                let pricingTemplate;
                if (scope.brand && scope.brand._id && scope.brand.settings.pricing_templates) {
                    if (scope.company && scope.company._id) {
                        pricingTemplate = scope.company.__settings.__pricing_template;
                    } else {
                        const user = scope.brand._users.find((user) => user.ref === this._id) || {};
                        pricingTemplate = user.pricing_template;
                    }
                }
                return pricingTemplate && pricingTemplate !== "undefined" ? pricingTemplate : "";
            },
        },
    },

    initialize() {
        // now that we moved the 'session' props out, lets save the session model
        this.session = new Session();

        this.session.on("sync", () => {
            if (!this.isSignedIn()) {
                this.clear(); // clear this object if logged out
            }
        });

        app.on("legacy:user-account-updated", (data) => {
            const userData = data.data;
            this.set({
                using_temp_password: userData.using_temp_password,
                name: userData.name,
                email: userData.email,
            });
        });

        this.on("sync", () => {
            const logUser = this.actual_user || this;

            const userData = {
                id: logUser._id,
                email: logUser.email,
                name: logUser.name,
                account_type: logUser.account_type,
            };

            Sentry.configureScope((scope) => {
                scope.setUser(userData);
            });
            // initPendo(userData);
        });

        return this;
    },

    bindLDClient(client) {
        this.ldClient = client;
    },

    login(credentials, callback) {
        const onSuccess = (model, data = {}) => {
            this.set(data);
            this.trigger("sync");
            this.session.trigger("sync");
            const dashboardRedirect = this.account_type === "buyer" ? "/dashboard" : "/brands";
            const redirect = credentials.redirect ? credentials.redirect : dashboardRedirect;

            if (this.store) {
                this.store.dispatch(toggleLoginDialog(false));
            }

            if (credentials.redirect && (this.account_type !== "buyer" || credentials.redirect !== "/brands")) {
                window.location = redirect;
            } else if (this.account_type === "buyer") {
                if (this.hasCollectionsAccess()) {
                    window.location = DRIVE_LINK;
                } else {
                    window.location = "/dashboard";
                }
            } else {
                window.location = "/brands";
            }
            callback(null);
        };

        const onError = () => {
            const err = new Error(Globalize.formatMessage("Invalid email and or password"));
            callback(err);
        };

        this.session.save(credentials, {
            success: onSuccess,
            error: onError,
        });
    },

    loginPartially(accesstoken, errorHandler) {
        this.session.save(
            { accesstoken },
            {
                success: (model, data = {}) => {
                    this.set(data);
                    this.trigger("sync");
                    this.session.trigger("sync");

                    errorHandler(null);
                },

                error: () => {
                    const err = new Error(Globalize.formatMessage("Failed to login"));
                    errorHandler(err);
                },
            }
        );
    },

    logout(remember = false) {
        if (remember && ["/", "/login"].indexOf(global.location.pathname) === -1) {
            remember = global.location.pathname;
        } else {
            remember = false;
        }
        this.unset("authStatus");
        if (this.store) {
            this.store.dispatch(userLogout());
        }
        this.is_logging_out = true;
        this.session.destroy({
            success: () => {
                this.clearSession(remember);

                if (this.ldClient) {
                    this.ldClient.identify({
                        key: LAUNCH_DARKLY_ANONYMOUS_USER_KEY,
                        anonymous: true,
                    });
                }

                if (config.LSID_FEATURE_LSID === "true" || config.LSID_FEATURE_SSO === "true") {
                    const apiRoot = config.api;
                    const callbackUrl = apiRoot.includes(window.location.protocol)
                        ? new URL(apiRoot)
                        : new URL(apiRoot, window.location.origin);
                    const lsidLogoutUrl = new URL("/auth/logout", config.LSID_API_ROOT);
                    const logoutPath = callbackUrl.href.endsWith("/") ? "logout" : "/logout";
                    lsidLogoutUrl.searchParams.append("redirect_uri", callbackUrl.href + logoutPath);
                    window.location.assign(lsidLogoutUrl);
                }
            },
            error: () => {
                this.clearSession(remember);
            },
        });
    },

    clearSession(remember = false) {
        this.trigger("logout");
        this.session.clear();
        this.clear();
        const store = getStore();
        const { scope } = store.getState();
        if (scope.brand && scope.brand._id) {
            store.dispatch(activeBrandClear());
        }
        if (scope.company && scope.company._id) {
            store.dispatch(activeCompanyClear());
        }

        let query;
        if (remember) {
            query = { redirect: remember };
        }

        if (window.auryc && window.auryc.clearUserCookie) {
            window.auryc.clearUserCookie();
        }

        logoutChurnZero();

        // remove all campaign cookies
        const apiPath = config.api.replace("https://", ".").replace("http://", ".");
        Object.keys(cookie.select(/user_.*_campaign_for_.*/g)).forEach((name) =>
            cookie.remove(name, { path: "/", domain: apiPath })
        );
        browserHistory.replace({ pathname: "/", query });
        this.trigger("change");
    },

    // Register merchant Id and user Id in launchdarkly so we can use them in `rules`
    async handleRegisterUserToLaunchdarkly(options = {}) {
        const scopeBrand = this.handleGetScopeBrand();
        const logUser = this.actual_user || this;
        const [firstName, lastName] = logUser.name.split(" ");
        const groupId = this.handleGetGroupId();
        const { merchantId, assortmentBagVersion, posOrderExport } = options;

        await this.ldClient.identify({
            // On impersonated session / userid is undefined we fall back on the ANONYMOUS key
            key: logUser?._id || LAUNCH_DARKLY_ANONYMOUS_USER_KEY,
            firstName,
            lastName,
            email: logUser?.email,
            custom: {
                account_type: logUser?.account_type,
                brand_id: groupId,
                brand_name: scopeBrand?.name,
                // Register merchant Id in launchdarkly so we can use them in `rules`
                merchantId: merchantId ?? this.getLastMerchantId(),
                assortmentBagVersion,
                posOrderExport: posOrderExport,
                isAssortmentBagVersionAvailable: !!assortmentBagVersion,
            },
        });
    },

    handleGetScopeBrand() {
        const { scope } = getStore().getState();

        return scope.brand;
    },

    handleGetGroupId() {
        const scopeBrand = this.handleGetScopeBrand();

        return scopeBrand && scopeBrand._id ? scopeBrand._id : undefined;
    },

    handleShowSurvey() {
        const logUser = this.actual_user || this;
        global.analytics.track("Successfully exported an order to POS", {
            name: logUser.name,
            email: logUser.email,
            role: logUser.account_type,
        });
    },

    async handleBrandChange() {
        // Analytics
        const store = getStore();

        const { scope, lsid } = store.getState();

        // reset insights state
        store.dispatch(clearInsightsData());

        if (this.isSignedIn()) {
            const scopeBrand = this.handleGetScopeBrand();
            const logUser = this.actual_user || this;
            const groupId = this.handleGetGroupId();
            const retailAccountId = lsid?.accountGrants?.retail?.[0] ?? "";
            const posOrderExport =
                lsid?.accountFeatures?.[retailAccountId]?.includes(
                    AccountFeatureFlags.LSPD_RSERIES_NUORDER_INTEGRATION
                ) ?? false;
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            global.analytics.identify(
                logUser._id,
                {
                    name: logUser.name,
                    email: logUser.email,
                    ofid: logUser.email, // for salesforce
                    role: logUser.account_type,
                    // Tracking analytics
                    site_connections: Array.isArray(logUser.site_connections)
                        ? logUser.site_connections.join(", ")
                        : undefined,
                    source: logUser.source,
                    time_zone: userTimeZone,
                    subscription_tier: scopeBrand?.settings?.subscription_tier,
                },
                { groupId }
            );

            if (this.ldClient) {
                await this.handleRegisterUserToLaunchdarkly({ posOrderExport: posOrderExport });
            }

            if (groupId) {
                scopeBrand.trackGroup();
                // Add more info to help fix errors
                Sentry.setExtra("brandId", scope.brandId);
                Sentry.setExtra("brandName", scopeBrand.name);
            }
        }
    },

    hasPermission(permission) {
        const { scope } = getStore().getState();
        if (this.account_type === "nuorder") {
            const permissionsToCheck = ["__can_manage_super_users__"];

            if (permissionsToCheck.includes(permission)) {
                return this.permissions.includes(permission);
            }

            return true;
        } else if (scope.brand && scope.brand._id) {
            return scope.brand.userHasPermission(this, permission, scope);
        }
        return false;
    },

    canViewBrand() {
        const { scope } = getStore().getState();
        if (this.account_type === "buyer" && scope.brand && scope.brand._id) {
            return !scope.brand.hasPreference("hide_from_buyers");
        } else {
            return true;
        }
    },

    canViewGallery() {
        const { scope } = getStore().getState();
        if (!scope.brand || !scope.brand._id) {
            return true;
        }
        return scope.brand.canDisplayProductsToUser(this);
    },

    canDefaultToWholesale() {
        const { scope } = getStore().getState();
        const hasWholesaleAccess = scope.brand && scope.brand.available_modules.indexOf("wholesale") !== -1;

        return hasWholesaleAccess;
    },

    userCanPlaceOrders() {
        const { scope } = getStore().getState();
        return (
            scope.brand &&
            scope.brand._id &&
            !scope.brand.hasPreference("no_orders") &&
            (this.account_type !== "buyer" || !scope.brand.hasPreference("no_buyer_orders"))
        );
    },

    isSignedIn(minimalAuthStatus = PARTIAL_AUTH) {
        if (!isValidAuthStatus(minimalAuthStatus)) {
            throw Error("Invalid minimalAuthStatus argument");
        }

        // Be careful. It means at least minimal auth status
        // so FULL_AUTH will be true for isSignedIn(PARTIAL_AUTH)
        return this.authStatus !== NO_AUTH && this.authStatus >= minimalAuthStatus;
    },

    isSignedInFully() {
        return this.authStatus === FULL_AUTH;
    },

    isSignedInPartially() {
        return this.authStatus === PARTIAL_AUTH;
    },

    getLastMerchantId() {
        return localStorage.getItem(LAST_MERCHANT_ID);
    },

    setLastMerchantId(id) {
        localStorage.setItem(LAST_MERCHANT_ID, id);
    },

    trackAction(action, data) {
        const { scope } = getStore().getState();
        if (scope.brand && scope.brand._id) {
            scope.brand.trackAction(action, data);
        }
    },

    nuAnalytics(action, params) {
        this.trackAction("nuAnalytics tracking", {
            nuAnalyticsFn: action,
            nuAnalyticsParams: params,
        });
    },

    nuAnalyticsLocation() {
        let location = "item";
        if (global.location.pathname.includes("linesheet")) {
            location = "linesheet";
        } else if (global.location.pathname.includes("list")) {
            location = "list";
        } else if (global.location.pathname.includes("gallery")) {
            location = "gallery";
        }
        return location;
    },

    getViewPref(key) {
        return this.view_preferences ? this.view_preferences[key] : undefined;
    },

    setViewPref(key, value, { onSuccess, onError } = {}) {
        // save in-memory clientside data
        this.view_preferences = this.view_preferences || {};
        this.view_preferences[key] = value;

        // send server patch-request with changes only. I do not handle response by intention
        const viewPrefsChanges = {};
        viewPrefsChanges[key] = value;
        this.save({ view_preferences: viewPrefsChanges }, { patch: true, success: onSuccess, error: onError });
    },

    isGuestBuyer() {
        const { scope } = getStore().getState();
        const hasNoCompanyConnection =
            (!scope.company || !scope.company._id) && scope.companies && scope.companies.length === 0;
        return this.account_type === "buyer" && (!scope.brand || hasNoCompanyConnection);
    },
    canBrowseBrands() {
        return this.account_type !== "buyer" || this.buyer_form_submitted || (this.retailers && this.retailers.length);
    },
    isPendingConnection(requestedConnections, brandId) {
        let isPending = false;
        if (this.account_type === "buyer") {
            const cr = _.find(requestedConnections, (cr) => cr.brand_id.toString() === brandId.toString()) || {};
            isPending = cr.status === ConnectionStatus.PENDING || cr.status === ConnectionStatus.REVIEW;
        }
        return isPending;
    },
    canConnectToBrand() {
        return !!(this.isGuestBuyer() && this.retailers && this.retailers.length);
    },
    setActiveBrand(brand) {
        this.activeBrand = brand;

        // Save active brand as last
        if (brand._id && !brand.disabled && this.last_brand !== brand._id.toString()) {
            this.save({ last_brand: brand._id }, { patch: true });
        }
    },
    isBuyer() {
        return this.account_type === "buyer";
    },
    isBrandUser() {
        return this.account_type === "brand";
    },
    isNuOrderUser() {
        return this.account_type === "nuorder";
    },
    hasMerchantOrganization() {
        return Boolean(
            this?.retailers && this?.retailers.some((retailer) => Boolean(retailer?.__merchant_organization))
        );
    },

    _is_logging_out: false,

    get is_logging_out() {
        return this._is_logging_out;
    },

    set is_logging_out(value) {
        this._is_logging_out = value;
    },

    hasCollectionsAccess() {
        const hasAssortmentsMerchants =
            this.merchant_connections &&
            this.merchant_connections.find((mc) => mc.capabilities && mc.capabilities.includes("assortments"));
        return hasAssortmentsMerchants && this.access_collections;
    },
});
const MeInstance = new Me(); // singleton
export default MeInstance;
export const BaseMe = Me;
